/**
 * Which voice a language/gender pair gets, and which lines it can take.
 *
 * Byte-for-byte the same rule as mobile's `index.ts` (`voiceFor`/`canSpeak`)
 * — same voice names, same models bucket, same restriction. A phrase one
 * platform speaks offline and the other sends to the network is a
 * difference users notice as "the web app sounds different from the app".
 */
import type { VoiceGender } from '../baba-api'

/** Native Hebrew voices, plus the English pair for non-Hebrew lines. */
export const VOICES = {
  hebrewMale: 'omer',
  hebrewFemale: 'liat',
  englishMale: 'michael',
  englishFemale: 'anna',
} as const

export interface Voice {
  name: string
  /** Hebrew reads IPA through the adapter; English reads ordinary spelling. */
  phonemes: boolean
}

function isHebrewLanguage(language: string): boolean {
  const lower = language.toLowerCase()
  return lower.startsWith('he') || lower.startsWith('iw')
}

export function voiceFor(language: string, gender: VoiceGender): Voice {
  if (isHebrewLanguage(language)) {
    return { name: gender === 'male' ? VOICES.hebrewMale : VOICES.hebrewFemale, phonemes: true }
  }
  return { name: gender === 'male' ? VOICES.englishMale : VOICES.englishFemale, phonemes: false }
}

const LATIN = /[A-Za-zÀ-ɏ]/

/**
 * Whether this exact line can be spoken offline without a quality cliff.
 *
 * Hebrew mixed with Latin words is the one case handed back. Upstream
 * phonemizes the Latin run with espeak, which is GPL and was deliberately
 * not vendored (see vendor/README.md), so those words would otherwise go
 * through SentencePiece with the Hebrew adapter switched on and come out as
 * noise — the network voice is the better fallback for that line.
 */
export function canSpeakOffline(text: string, language: string): boolean {
  if (!isHebrewLanguage(language)) return true
  return !LATIN.test(text)
}
